import React from 'react';
import { ScrollView, StyleSheet, View, Platform, KeyboardAvoidingView } from 'react-native';
import { Surface, Text, TextInput, Button, Dialog, Portal, useTheme } from 'react-native-paper';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { CustomDatePicker } from './CustomDatePicker';
import { saveOwnerDriverProfile, checkNifAvailable, checkLicenseNumberAvailable } from '../api/apiService';
import type { DriverProfileBoundaryResponse } from '../api/apiService';
import { OperatorLicenseInput } from './OperatorLicenseInput';
import { CountryCodeSelector } from './CountryCodeSelector';
import { NifInput } from './NifInput';

const completeProfileSchema = z.object({
  full_name: z.string().min(1, { message: 'Nome completo é obrigatório' }),
  phone_number: z.string().min(1, { message: 'Telefone é obrigatório' }),
  nif: z
    .string()
    .transform((val) => val.replace(/\D/g, ''))
    .pipe(
      z
        .string()
        .min(1, { message: 'NIF é obrigatório' })
        .length(9, { message: 'NIF deve ter 9 dígitos' }),
    ),
  address: z.string().min(1, { message: 'Endereço é obrigatório' }),
  cmtvde: z
    .string()
    .min(1, { message: 'Número da CMTVDE é obrigatório' })
    .regex(/^\d+\/\d{4}$/, { message: 'Formato inválido (N+/YYYY)' }),
  date_of_birth: z.string().min(1, { message: 'Data de nascimento é obrigatória' }),
});

type CompleteProfileForm = z.infer<typeof completeProfileSchema>;

export interface CompleteDriverProfileFormProps {
  onSuccess: (result?: DriverProfileBoundaryResponse) => void | Promise<void>;
  /**
   * Optional submit label override. The owner become-driver modal submits as
   * "Tornar-me motorista" (D-046, 2026-10-11); the generic completion page
   * keeps the default "Guardar perfil".
   */
  submitLabel?: string;
}

export function CompleteDriverProfileForm({ onSuccess, submitLabel }: CompleteDriverProfileFormProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [countryCode, setCountryCode] = React.useState('+351');
  const [menuVisible, setMenuVisible] = React.useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = React.useState<string | null>(null);

  const { control, handleSubmit, formState: { errors, isSubmitting }, setError, clearErrors } = useForm<CompleteProfileForm>({
    resolver: zodResolver(completeProfileSchema),
    defaultValues: {
      full_name: '',
      phone_number: '',
      nif: '',
      address: '',
      cmtvde: '',
      date_of_birth: '',
    },
  });

  const handleNifBlur = React.useCallback(async (value: string, rhfOnBlur?: () => void) => {
    try {
      const digits = String(value || '').replace(/\D/g, '');
      if (digits.length !== 9) {
        rhfOnBlur?.();
        return;
      }
      const res = await checkNifAvailable(digits);
      if (res.ok && res.data && res.data.available === false) {
        setError('nif', { type: 'server', message: res.data.reason || 'Este NIF já está em uso.' });
      }
    } catch {
      // Uniqueness checks are advisory; the submit endpoint remains authoritative.
    } finally {
      rhfOnBlur?.();
    }
  }, [setError]);

  const handleCmtvdeBlur = React.useCallback(async (value: string, rhfOnBlur?: () => void) => {
    try {
      const text = String(value || '').trim();
      if (!/^\d+\/\d{4}$/.test(text)) {
        rhfOnBlur?.();
        return;
      }
      const res = await checkLicenseNumberAvailable(text);
      if (res.ok && res.data && res.data.available === false) {
        setError('cmtvde', { type: 'server', message: res.data.reason || 'Este CMTVDE já está em uso.' });
      }
    } catch {
      // Uniqueness checks are advisory; the submit endpoint remains authoritative.
    } finally {
      rhfOnBlur?.();
    }
  }, [setError]);

  const onSubmit: SubmitHandler<CompleteProfileForm> = async (data) => {
    try {
      const response = await saveOwnerDriverProfile({
        full_name: data.full_name,
        phone_number: `${countryCode}${data.phone_number}`,
        nif: data.nif.replace(/\D/g, ''),
        address: data.address,
        license_number: data.cmtvde,
        date_of_birth: data.date_of_birth,
      });
      if (response.ok) {
        await onSuccess(response.data as DriverProfileBoundaryResponse);
        return;
      }

      const errData = response.data as any;
      const detail = errData?.detail || errData?.message;
      const fieldMap: Record<string, keyof CompleteProfileForm> = {
        full_name: 'full_name',
        phone_number: 'phone_number',
        nif: 'nif',
        address: 'address',
        license_number: 'cmtvde',
        date_of_birth: 'date_of_birth',
      };
      let hasFieldErrors = false;
      Object.keys(fieldMap).forEach((key) => {
        const formKey = fieldMap[key];
        const messages = errData?.[key];
        if (messages) {
          hasFieldErrors = true;
          const message = Array.isArray(messages) ? messages[0] : String(messages);
          setError(formKey, { type: 'server', message });
        }
      });
      if (detail && !hasFieldErrors) {
        setErrorDialogMessage(String(detail));
      } else if (!hasFieldErrors) {
        setErrorDialogMessage(t('completeDriverProfile.genericError', 'Erro ao guardar o perfil.'));
      }
    } catch (error) {
      console.error(error);
      setErrorDialogMessage(t('completeDriverProfile.networkError', 'Erro de rede ao guardar o perfil.'));
    }
  };

  return (
    <>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoidingView}
        keyboardVerticalOffset={Platform.OS === 'ios' ? 64 : 20}
      >
        <ScrollView contentContainerStyle={styles.container} keyboardShouldPersistTaps="handled">
          <Surface style={styles.surface}>
          <Controller
            control={control}
            name="full_name"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <TextInput
                  label={t('fullNamePlaceholder', 'Nome completo')}
                  mode="outlined"
                  onBlur={onBlur}
                  onChangeText={onChange}
                  value={value}
                  error={!!errors.full_name}
                  style={styles.input}
                  testID="complete-driver-full-name"
                />
                {errors.full_name && <Text style={[styles.error, { color: theme.colors.error }]}>{errors.full_name.message}</Text>}
              </>
            )}
          />
          <Controller
            control={control}
            name="phone_number"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <View style={styles.phoneRow}>
                  <Button mode="outlined" onPress={() => setMenuVisible(true)} style={styles.countryButton}>
                    {countryCode}
                  </Button>
                  <TextInput
                    label={t('phonePlaceholder', 'Telefone')}
                    mode="outlined"
                    keyboardType="phone-pad"
                    onBlur={onBlur}
                    onChangeText={onChange}
                    value={value}
                    error={!!errors.phone_number}
                    style={styles.phoneInput}
                    maxLength={15}
                    testID="complete-driver-phone"
                  />
                </View>
                <CountryCodeSelector
                  visible={menuVisible}
                  onDismiss={() => setMenuVisible(false)}
                  onSelect={setCountryCode}
                  selectedCode={countryCode}
                />
                {errors.phone_number && <Text style={[styles.error, { color: theme.colors.error }]}>{errors.phone_number.message}</Text>}
              </>
            )}
          />
          <Controller
            control={control}
            name="nif"
            render={({ field: { onChange, onBlur, value } }) => (
              <NifInput
                value={value}
                onChange={(text) => {
                  clearErrors('nif');
                  onChange(text);
                }}
                onBlur={() => handleNifBlur(value, onBlur)}
                style={styles.input}
                errorText={errors.nif?.message}
                testID="complete-driver-nif"
              />
            )}
          />
          <Controller
            control={control}
            name="address"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <TextInput
                  label={t('addressPlaceholder', 'Endereço')}
                  mode="outlined"
                  onBlur={onBlur}
                  onChangeText={onChange}
                  value={value}
                  error={!!errors.address}
                  style={styles.input}
                  testID="complete-driver-address"
                />
                {errors.address && <Text style={[styles.error, { color: theme.colors.error }]}>{errors.address.message}</Text>}
              </>
            )}
          />
          <Controller
            control={control}
            name="cmtvde"
            render={({ field: { onChange, onBlur, value } }) => (
              <>
                <OperatorLicenseInput
                  label={t('cmtvdePlaceholder', 'Número da CMTVDE')}
                  value={value}
                  onChangeText={(text) => {
                    clearErrors('cmtvde');
                    onChange(text);
                  }}
                  onBlur={() => handleCmtvdeBlur(value, onBlur)}
                  error={!!errors.cmtvde}
                  style={styles.input}
                  testID="complete-driver-cmtvde"
                />
                {errors.cmtvde && <Text style={[styles.error, { color: theme.colors.error }]}>{errors.cmtvde.message}</Text>}
              </>
            )}
          />
          <Controller
            control={control}
            name="date_of_birth"
            render={({ field: { value, onChange } }) => (
              <>
                <CustomDatePicker
                  label={t('dobPlaceholder', 'Data de Nascimento')}
                  value={value}
                  onChange={onChange}
                  error={!!errors.date_of_birth}
                  testID="complete-driver-date-of-birth"
                />
                {errors.date_of_birth && <Text style={[styles.error, { color: theme.colors.error }]}>{errors.date_of_birth.message}</Text>}
              </>
            )}
          />
          <Button
            mode="contained"
            onPress={handleSubmit(onSubmit)}
            loading={isSubmitting}
            disabled={isSubmitting}
            style={styles.button}
            testID="complete-driver-submit"
          >
            {submitLabel ?? t('completeDriverProfile.submit', 'Guardar perfil')}
          </Button>
        </Surface>
      </ScrollView>
      </KeyboardAvoidingView>
      <Portal>
        <Dialog
          visible={errorDialogMessage !== null}
          onDismiss={() => setErrorDialogMessage(null)}
          testID="complete-driver-profile-error-dialog"
        >
          <Dialog.Title>{t('completeDriverProfile.errorTitle', 'Não foi possível guardar o perfil')}</Dialog.Title>
          <Dialog.Content>
            <Text>{errorDialogMessage}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setErrorDialogMessage(null)}>
              {t('close', 'Fechar')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </>
  );
}

const styles = StyleSheet.create({
  keyboardAvoidingView: { flex: 1 },
  container: {
    flexGrow: 1,
    padding: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  surface: {
    width: '100%',
    maxWidth: 400,
    padding: 16,
    borderRadius: 8,
  },
  input: { marginBottom: 12 },
  phoneRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 12 },
  countryButton: { marginRight: 10, minWidth: 80 },
  phoneInput: { flex: 1 },
  button: { marginTop: 16 },
  error: { marginBottom: 8 },
});
